import { useTour } from './TourContext'
import { useTranslation } from '../../i18n/LanguageContext'
import type { TourPlacement } from './types'

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

interface TourCardProps {
  targetRect: TargetRect
  visible: boolean
}

const CARD_WIDTH = 288
const CARD_GAP = 16

function getCardStyle(
  placement: TourPlacement,
  rect: TargetRect,
): React.CSSProperties {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const isMobile = vw < 640

  if (isMobile) {
    return {
      position: 'fixed',
      bottom: 16,
      left: 12,
      right: 12,
      width: 'auto',
    }
  }

  let top = 0
  let left = 0

  switch (placement) {
    case 'right': {
      left = rect.left + rect.width + CARD_GAP
      if (left + CARD_WIDTH > vw) left = rect.left - CARD_WIDTH - CARD_GAP
      top = rect.top + rect.height / 2 - 90
      break
    }
    case 'left': {
      left = rect.left - CARD_WIDTH - CARD_GAP
      if (left < 0) left = rect.left + rect.width + CARD_GAP
      top = rect.top + rect.height / 2 - 90
      break
    }
    case 'bottom': {
      top = rect.top + rect.height + CARD_GAP
      left = rect.left + rect.width / 2 - CARD_WIDTH / 2
      if (top + 220 > vh) top = rect.top - 220 - CARD_GAP
      break
    }
    case 'top': {
      top = rect.top - 220 - CARD_GAP
      if (top < 0) top = rect.top + rect.height + CARD_GAP
      left = rect.left + rect.width / 2 - CARD_WIDTH / 2
      break
    }
  }

  top = Math.max(8, Math.min(top, vh - 230))
  left = Math.max(8, Math.min(left, vw - CARD_WIDTH - 8))

  return { position: 'fixed', top, left, width: CARD_WIDTH }
}

// Step-specific gradient themes (pastel colors)
const STEP_GRADIENTS: Record<string, {
  accent: string
  softBg: string
  button: string
  ring: string
}> = {
  'nav-meetings': {
accent: 'from-blue-300 to-sky-300',
    softBg: 'bg-blue-50 dark:bg-card',
    button: 'from-blue-400 to-sky-400 hover:from-blue-500 hover:to-sky-500',
    ring: 'ring-blue-200/60 dark:ring-border'
  },
  'nav-chat': {
    accent: 'from-emerald-300 to-teal-300',
    softBg: 'bg-emerald-50 dark:bg-card',
    button: 'from-emerald-400 to-teal-400 hover:from-emerald-500 hover:to-teal-500',
    ring: 'ring-emerald-200/60 dark:ring-border'
  },
  'nav-webinar': {
    accent: 'from-violet-300 to-purple-300',
    softBg: 'bg-violet-50 dark:bg-card',
    button: 'from-violet-400 to-purple-400 hover:from-violet-500 hover:to-purple-500',
    ring: 'ring-violet-200/60 dark:ring-border'
  },
  'nav-calendar': {
    accent: 'from-rose-300 to-pink-300',
    softBg: 'bg-rose-50 dark:bg-card',
    button: 'from-rose-400 to-pink-400 hover:from-rose-500 hover:to-pink-500',
    ring: 'ring-rose-200/60 dark:ring-border'
  },
  'nav-rooms': {
    accent: 'from-amber-300 to-yellow-300',
    softBg: 'bg-amber-50 dark:bg-card',
    button: 'from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500',
    ring: 'ring-amber-200/60 dark:ring-border'
  },
  'nav-settings': {
    accent: 'from-slate-300 to-gray-300',
    softBg: 'bg-slate-50 dark:bg-card',
    button: 'from-slate-400 to-gray-400 hover:from-slate-500 hover:to-gray-500',
    ring: 'ring-slate-200/60 dark:ring-border'
  },
  'action-instant': {
    accent: 'from-green-300 to-lime-300',
    softBg: 'bg-green-50 dark:bg-card',
    button: 'from-green-400 to-lime-400 hover:from-green-500 hover:to-lime-500',
    ring: 'ring-green-200/60 dark:ring-border'
  },
  'action-join': {
    accent: 'from-cyan-300 to-blue-300',
    softBg: 'bg-cyan-50 dark:bg-card',
    button: 'from-cyan-400 to-blue-400 hover:from-cyan-500 hover:to-blue-500',
    ring: 'ring-cyan-200/60 dark:ring-border'
  },
  'action-schedule': {
    accent: 'from-indigo-300 to-purple-300',
    softBg: 'bg-indigo-50 dark:bg-card',
    button: 'from-indigo-400 to-purple-400 hover:from-indigo-500 hover:to-purple-500',
    ring: 'ring-indigo-200/60 dark:ring-border'
  },
  'action-share': {
    accent: 'from-orange-300 to-red-300',
    softBg: 'bg-orange-50 dark:bg-card',
    button: 'from-orange-400 to-red-400 hover:from-orange-500 hover:to-red-500',
    ring: 'ring-orange-200/60 dark:ring-border'
  },
  'header-notification': {
    accent: 'from-pink-300 to-rose-300',
    softBg: 'bg-pink-50 dark:bg-card',
    button: 'from-pink-400 to-rose-400 hover:from-pink-500 hover:to-rose-500',
    ring: 'ring-pink-200/60 dark:ring-border'
  },
  'header-avatar': {
    accent: 'from-teal-300 to-cyan-300',
    softBg: 'bg-teal-50 dark:bg-card',
    button: 'from-teal-400 to-cyan-400 hover:from-teal-500 hover:to-cyan-500',
    ring: 'ring-teal-200/60 dark:ring-border'
  },
}

export default function TourCard({ targetRect, visible }: TourCardProps) {
  const t = useTranslation()
  const { currentStep, currentStepIndex, totalSteps, nextStep, skipTour, cancelTour } = useTour()

  if (!currentStep) return null

  const isLastStep = currentStepIndex === totalSteps - 1
  const progress = ((currentStepIndex + 1) / totalSteps) * 100
  const cardStyle = getCardStyle(currentStep.placement, targetRect)

  // Use a single theme for all steps (same as first Meetings card)
  const theme = STEP_GRADIENTS['nav-meetings']

  return (
    <div
      style={{
        ...cardStyle,
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(6px)',
        transition: 'all 0.3s ease',
        zIndex: 9999,
      }}
      className={`${theme.softBg} border border-border rounded-2xl shadow-lg ring-1 ${theme.ring} overflow-hidden`}
      onClick={e => e.stopPropagation()}
    >
      {/* Top accent bar */}
      <div
        className={`h-1.5 w-full bg-gradient-to-r ${theme.accent} rounded-t-2xl relative overflow-hidden`}
      >
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{
            transform: visible ? 'translateX(-100%)' : 'translateX(100%)',
            animation: visible ? 'slide 4s ease-in-out infinite' : 'none',
            opacity: 0.5,
          }}
        />
      </div>

      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex flex-col gap-0.5">

            <h3 className="text-sm font-bold text-foreground leading-snug">{t(currentStep.title)}</h3>
          </div>
          <button
            onClick={cancelTour}
            className="text-muted-foreground hover:text-foreground transition-colors w-6 h-6 flex items-center justify-center rounded-lg hover:bg-muted text-xs leading-none mt-0.5"
          >
            ✕
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-foreground/80 leading-relaxed mb-4">
          {t(currentStep.description)}
        </p>

        {/* Progress bar with gradient */}
        <div className="h-1.5 bg-black/8 dark:bg-card/8 rounded-full mb-3 overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${theme.accent} rounded-full shadow-sm`}
            style={{ 
              width: `${progress}%`, 
              transition: 'width 0.4s ease',
            }}
          />
        </div>

        {/* Enhanced group dots */}
        <div className="flex items-center gap-2 mb-4">
          {/* {groups.map((g, idx) => (
            <div
              key={g}
              className={`h-2 rounded-full transition-all duration-500 ${
                g === currentGroup 
                  ? `bg-gradient-to-r ${theme.accent} w-6 shadow-sm` 
                  : 'bg-muted w-2'
              }`}
              style={{
                transitionDelay: `${idx * 50}ms`
              }}
            />
          ))} */}
          <span className="ml-auto text-[10px] text-slate-600 dark:text-muted-foreground tabular-nums font-medium">
            {currentStepIndex + 1} / {totalSteps}
          </span>
        </div>

        {/* Enhanced action buttons */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={skipTour}
            className="text-xs text-slate-600 dark:text-muted-foreground hover:text-slate-800 dark:hover:text-foreground transition-all duration-200 px-3 py-2 rounded-xl hover:bg-black/5 dark:hover:bg-muted/50"
          >
            {t('tour.skipTour')}
          </button>
          <button
            onClick={nextStep}
            className={`text-xs font-semibold text-white bg-gradient-to-r ${theme.button} px-6 py-2.5 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95`}
            style={{
              animation: visible ? 'pulse 2.8s ease-in-out infinite' : 'none'
            }}
          >
            {isLastStep ? `${t('tour.finish')} 🎉` : `${t('tour.next')} →`}
          </button>
        </div>
      </div>
    </div>
  )
}
